import { api, uploadFormData } from '../../api';
import type {
  ContactConversation,
  ContactMessage,
} from '../../app/legacy-types';
import type { ContactCenterFilters } from './contactCenterModel';
import type { ContactActivity } from './MessageActions';
import type { ContactMediaAsset } from './SafeContactMedia';

export type ContactCenterOperator = {
  id: string;
  full_name: string;
  email: string;
  role: string;
  operator_status: string;
  extension?: string | null;
  is_online: boolean;
};

export type ContactRelationshipOptions = {
  companies: Array<{ id: string; name: string; full_name?: string | null; inn?: string | null }>;
  contacts: Array<{ id: string; company_id: string; full_name: string; email?: string | null; phone?: string | null; company_name: string }>;
  tickets: Array<{ id: string; number: number; display_number: string; subject: string; status: string; company_id: string; company_name: string }>;
};

export type ContactReplyTemplate = {
  id: string;
  name: string;
  body: string;
  preview: string;
  provider?: string | null;
  sort_order: number;
};

export type ContactConversationFile = {
  id: string;
  messageId: string;
  name: string;
  mimeType: string;
  size: number;
  source: 'STORED' | 'PROVIDER' | 'EMAIL';
  inline?: boolean;
  downloadUrl: string;
};

export const contactCenterKeys = {
  all: ['contact-center'] as const,
  conversations: (filters: ContactCenterFilters) => ['contact-center', 'conversations', filters] as const,
  conversationLists: () => ['contact-center', 'conversations'] as const,
  messages: (conversationId: string | null | undefined) => ['contact-center', 'messages', conversationId ?? null] as const,
  messageLists: () => ['contact-center', 'messages'] as const,
  activity: (conversationId: string | null | undefined) => ['contact-center', 'activity', conversationId ?? null] as const,
  operators: ['contact-center', 'operators'] as const,
  relationships: (search = '') => ['contact-center', 'relationships', search] as const,
  templates: (provider = '', conversationId = '') => ['contact-center', 'templates', provider, conversationId] as const,
  files: (conversationId: string, search = '') => ['contact-center', 'files', conversationId, search] as const,
  media: (provider = '') => ['contact-center', 'media', provider] as const,
};

function appendBoolean(params: URLSearchParams, name: string, value: boolean | undefined): void {
  if (typeof value === 'boolean') params.set(name, String(value));
}

export const contactCenterPath = {
  conversations(filters: ContactCenterFilters): string {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.provider && filters.provider !== 'ALL') params.set('provider', filters.provider);
    appendBoolean(params, 'mine', filters.mine);
    appendBoolean(params, 'unread', filters.unread);
    appendBoolean(params, 'unanswered', filters.unanswered);
    appendBoolean(params, 'archived', filters.archived);
    appendBoolean(params, 'spam', filters.spam);
    if (filters.search?.trim()) params.set('search', filters.search.trim());
    const query = params.toString();
    return `/contact-center/conversations${query ? `?${query}` : ''}`;
  },
};

export const contactCenterApi = {
  conversations: (filters: ContactCenterFilters, signal?: AbortSignal) => api<ContactConversation[]>(contactCenterPath.conversations(filters), { signal }),
  messages: (conversationId: string, signal?: AbortSignal) => api<ContactMessage[]>(`/contact-center/conversations/${conversationId}/messages`, { signal }),
  activity: (conversationId: string, signal?: AbortSignal) => api<ContactActivity[]>(`/contact-center/conversations/${conversationId}/activity`, { signal }),
  resolveMailMessage: (mailMessageId: string, signal?: AbortSignal) => api<{ conversationId: string; messageId: string }>(`/contact-center/mail/messages/${encodeURIComponent(mailMessageId)}/link`, { signal }),
  operators: (signal?: AbortSignal) => api<ContactCenterOperator[]>('/contact-center/operators', { signal }),
  relationships: (search = '', signal?: AbortSignal) => api<ContactRelationshipOptions>(`/contact-center/relationship-options${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`, { signal }),
  templates: (provider: string, conversationId: string, signal?: AbortSignal) => api<ContactReplyTemplate[]>(`/contact-center/reply-templates?provider=${encodeURIComponent(provider)}&conversationId=${encodeURIComponent(conversationId)}`, { signal }),
  files: (conversationId: string, search = '', signal?: AbortSignal) => api<ContactConversationFile[]>(`/contact-center/conversations/${conversationId}/files${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`, { signal }),
  mediaLibrary: (provider: string, signal?: AbortSignal) => api<ContactMediaAsset[]>(`/contact-center/media/library?provider=${encodeURIComponent(provider)}`, { signal }),
  markRead: (conversationId: string) => api<{ success: boolean }>(`/contact-center/conversations/${conversationId}/read`, { method: 'POST' }),
  assign: (conversationId: string, userId?: string | null) => api<{ success: boolean }>(`/contact-center/conversations/${conversationId}/assign`, { method: 'PATCH', body: JSON.stringify({ userId: userId || '' }) }),
  setStatus: (conversationId: string, status: ContactConversation['status']) => api<{ success: boolean }>(`/contact-center/conversations/${conversationId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  linkTicket: (conversationId: string, ticketId?: string | null) => api(`/contact-center/conversations/${conversationId}/link-ticket`, { method: 'PATCH', body: JSON.stringify({ ticketId: ticketId || '' }) }),
  linkCustomer: (conversationId: string, companyId?: string | null, contactId?: string | null) => api(`/contact-center/conversations/${conversationId}/link-customer`, { method: 'PATCH', body: JSON.stringify({ companyId: companyId || '', contactId: contactId || '' }) }),
  removeConversation: (conversationId: string) => api(`/contact-center/conversations/${conversationId}`, { method: 'DELETE' }),
  editMessage: (messageId: string, body: string) => api(`/contact-center/messages/${messageId}`, { method: 'PATCH', body: JSON.stringify({ body }) }),
  deleteMessageEverywhere: (messageId: string) => api(`/contact-center/messages/${messageId}/everywhere`, { method: 'DELETE' }),
  reactToMessage: (messageId: string, reaction: string) => api(`/contact-center/messages/${messageId}/reactions`, { method: 'POST', body: JSON.stringify({ reaction }) }),
  retryAction: (actionId: string) => api(`/contact-center/actions/${actionId}/retry`, { method: 'POST' }),
  sendLibraryAsset: (conversationId: string, assetId: string) => api<ContactMessage>(`/contact-center/conversations/${conversationId}/library-assets/${assetId}/send`, { method: 'POST' }),
  sendMessage: async (conversationId: string, body: string, files: File[]) => {
    if (!files.length) return api<ContactMessage>(`/contact-center/conversations/${conversationId}/messages`, { method: 'POST', body: JSON.stringify({ body }) });
    const form = new FormData();
    form.append('body', body);
    files.forEach(file => form.append('files', file, file.name));
    return uploadFormData<ContactMessage>(`/contact-center/conversations/${conversationId}/messages/with-files`, form);
  },
};
